import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { ConfigurationService } from '../shared/configuration.service';
import { JwtHelper } from './jwt-helper.service';
import { IdToken, LoginResponse } from 'src/app/models/authentication/login-response.model';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  public permissions: string[];
  public userId: string | null = null;
  public defaultCompanyId: string | undefined;
  private readonly clientId = 'pm_spa';
  private readonly scope = 'openid offline_access';
  private useLocalStorage = true; // default

  constructor(private http: HttpClient) {
    this.permissions = this.getPermissions();
  }

  // ✅ Pass rememberMe flag from component
  login(username: string, password: string, rememberMe: boolean): Observable<LoginResponse> {
    this.useLocalStorage = rememberMe;

    const apiUrl = ConfigurationService.config.API_URL + '/connect/token';
    const body = new HttpParams()
      .set('grant_type', 'password')
      .set('username', username)
      .set('password', password)
      .append('client_id', this.clientId)
      .set('scope', this.scope);

    return this.http.post<LoginResponse>(apiUrl, body.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    }).pipe(
      map(tokens => {
        this.storeTokens(tokens, rememberMe);
        this.permissions = this.getPermissions();
        return tokens;
      })
    );
  }

  refreshToken(): Observable<LoginResponse> {
    const apiUrl = ConfigurationService.config.API_URL + '/connect/token';
    const refreshToken = this.getItem('refresh_token');
    if (!refreshToken) return throwError(() => 'No refresh token found');

    const body = new HttpParams()
      .set('grant_type', 'refresh_token')
      .set('refresh_token', refreshToken)
      .append('client_id', this.clientId)
      .set('scope', this.scope);;

    return this.http.post<LoginResponse>(apiUrl, body.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    }).pipe(
      map(tokens => {
        // Use the same storage type as before
        const rememberMe = !!localStorage.getItem('remember_me');
        this.storeTokens(tokens, rememberMe);
        this.permissions = this.getPermissions();
        return tokens;
      })
    );
  }

  // ✅ Store in correct storage
  private storeTokens(tokens: LoginResponse, rememberMe: boolean) {
    const storage = rememberMe ? localStorage : sessionStorage;

    storage.setItem('id_token', tokens.id_token);
    storage.setItem('access_token', tokens.access_token);
    storage.setItem('refresh_token', tokens.refresh_token);
    storage.setItem('expires_at', (Date.now() + tokens.expires_in * 1000).toString());

    // Save flag in localStorage to reuse after refresh
    localStorage.setItem('remember_me', rememberMe ? 'true' : 'false');
  }

  // ✅ Helper: read from correct storage
  private getItem(key: string): string | null {
    const rememberMe = localStorage.getItem('remember_me') === 'true';
    const storage = rememberMe ? localStorage : sessionStorage;
    return storage.getItem(key);
  }

  getIdentityToken(): string | null {
    return this.getItem('id_token');
  }

  getAccessToken(): string | null {
    return this.getItem('access_token');
  }


  getPermissions(): string[] {
    const idToken = this.getIdentityToken();
    if (!idToken) return [];

    const jwtHelper = new JwtHelper();
    const decodedIdToken = jwtHelper.decodeToken(idToken) as IdToken;

    this.userId = decodedIdToken['user-id'];

    const companyId = decodedIdToken.permissions?.find(p => p.includes('DefaultCompany'));
    this.defaultCompanyId = companyId?.split(':')[1];

    return Array.isArray(decodedIdToken.permissions)
      ? decodedIdToken.permissions
      : [decodedIdToken.permissions];
  }

  hasPermission(required: string): boolean {
    return this.getPermissions().includes(required);
  }

  isTokenExpired(): boolean {
    const expiresAt = this.getItem('expires_at');
    return !expiresAt || Date.now() > parseInt(expiresAt, 10);
  }

  // ✅ Clear both storages
  logout(): void {
    localStorage.removeItem('id_token');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('expires_at');
    localStorage.removeItem('remember_me');

    sessionStorage.clear();
  }

  forgotPassword(email: string): Observable<any> {
    return this.http.post(`${ConfigurationService.config.API_URL}/api/Authorization/forgot-password`, { email });
  }

  setPassword(userId: string, token: string, newPassword: string): Observable<any> {
    return this.http.post(`${ConfigurationService.config.API_URL}/api/Users/set-password`, { userId, token, newPassword });
  }



}
